import React, {ReactNode, useContext, useEffect, useState} from "react";
import {createUseStyles, useTheme} from "react-jss";
import MultipleChoiceInput from "./inputs/MultipleChoiceInput";
import SortInput from "./inputs/SortInput";
import NumberInput from "./inputs/NumberInput";
import PhraseInput from "./inputs/PhraseInput";
import TextPrompt from "./prompts/TextPrompt";
import HintPrompt from "./prompts/HintPrompt";
import MultipleChoiceDisplay from "./display/MultipleChoiceDisplay";
import NumberDisplay from "./display/NumberDisplay";
import SortDisplay from "./display/SortDisplay";
import PhraseDisplay from "./display/PhraseDisplay";
import QuestionCounter from "../common/QuestionCounter";
import Explanation from "./common/Explanation";
import {AggregateResponses, GameOptions, ProcessedResponse, triviaStateDuration, TriviaStatus} from "../../state/state";
import {InputType, PromptType, QuestionDefinition} from "../../state/questions";
import {Theme} from "engage-client/theme";
import {Role} from "engage-common/types";
import {TriviaAction, TriviaMessage} from "../../messages/messages";
import {EngageAppContext} from "../../App";
import {TimeUnit, useCountdown} from "../../hooks/useCountdown";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import {BoxDimension} from "../common/VirtualHost";

export type QuestionInputProps = QuestionDisplayProps & {
	submitted: boolean
	onSubmit: (value:any) => void
	response?: ProcessedResponse
	pointsScored?: number
}

const QuestionInputs:{[key in InputType]: React.ComponentType<QuestionInputProps>} = {
	[InputType.MC]: MultipleChoiceInput,
	[InputType.Number]: NumberInput,
	[InputType.Sort]: SortInput,
	[InputType.Phrase]: PhraseInput
};

export type QuestionDisplayProps = {
	question: QuestionDefinition
	state: TriviaStatus
	aggregateResponses?:AggregateResponses
}

const QuestionDisplays:{[key in InputType]:React.ComponentType<QuestionDisplayProps>} = {
	[InputType.MC]: MultipleChoiceDisplay,
	[InputType.Number]: NumberDisplay,
	[InputType.Sort]: SortDisplay,
	[InputType.Phrase]: PhraseDisplay
};

//TODO:Need to figure out how to do multiple computed properties within one class
// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
	question: {
		position: "relative",
		display: "flex",
		flexDirection:"column",
		justifyContent: "flex-start",
		width: "100%",
		height: "100%",
		[theme.breakpoints.up("lg")]: {
			paddingTop: 20,
			paddingBottom: 44,
			maxWidth: 750,
		}
	},
	countdown : {
		textAlign: "center",
		minHeight:55,
		[theme.breakpoints.between("sm","md")]: {
			minHeight:65
		},
		[theme.breakpoints.only("lg")]: {
			marginLeft: props => props.showingHost ? -BoxDimension : 0,
		},
		marginTop: 10,
		marginBottom: 20,
	},
	questionCounter: {
		[theme.breakpoints.down("md")]: {
			position: "absolute",
			top: 9,
			left: 8
		},
		[theme.breakpoints.down("xs")]: {
			top:10
		},
		[theme.breakpoints.only("lg")]: {
			marginLeft: props => props.showingHost ? -BoxDimension : 0,
		},
		[theme.breakpoints.up("lg")]: {
			position: "absolute",
			bottom: 40,
			left: 0,
			right: 0,
			marginLeft: "auto",
			marginRight: "auto",
		},
	},
	bodyContainer: {
		flex: "0 1",
		padding: "10px 0",
		[theme.breakpoints.only("lg")]: {
			marginLeft: props => props.showingHost ? -BoxDimension : 0,
		},
		position: "relative"
	},

}));

type QuestionProps = {
	question:QuestionDefinition
	state:TriviaStatus
	gameOptions:GameOptions
	role:Role
	sendMessage?:(data:TriviaMessage) => void
	currentHint?:number
	questionNumber: number
	totalQuestions: number
	playerResponse?: ProcessedResponse
	playerScore?: number
	children?: ReactNode
	aggregateResponses?:AggregateResponses
	showingHost: boolean
}

export default function Question(props : QuestionProps) {
	const {state, role,question,sendMessage, currentHint, gameOptions, aggregateResponses,
			totalQuestions, questionNumber, playerResponse, playerScore, showingHost} = props;
	const [isSubmitted, setIsSubmitted] = useState(typeof playerResponse !== "undefined");
	useEffect(() => {
		setIsSubmitted(typeof playerResponse !== "undefined");
	}, [question.index, playerResponse]);

	const onSubmit = function(value: any) {
		if(question) {
			if(sendMessage) {
				sendMessage({type: TriviaAction.Respond, data: {questionIndex: question.index, value}});
			}
			setIsSubmitted(true);
		}
	};

	let questionBody:ReactNode;
	if(state === TriviaStatus.QuestionDetails && question.inputType !== InputType.Phrase) {
		questionBody = <Explanation question={question} aggregateResponses={aggregateResponses}
									showFastest={role === Role.host}/>;
	} else if(state !== TriviaStatus.QuestionStart || question.inputType === InputType.Phrase) {
		if(role === Role.user) {
			let Component = QuestionInputs[question.inputType];
			questionBody = <Component state={state} question={question} onSubmit={onSubmit}
									  submitted={isSubmitted} response={playerResponse} pointsScored={playerScore}
									  aggregateResponses={aggregateResponses} />;
		} else {
			let Component = QuestionDisplays[question.inputType];
			questionBody = <Component state={state} question={question} aggregateResponses={aggregateResponses}/>;
		}
	}

	const classes = useStyles({showingHost});

	return <div className={classes.question} data-qa="question-container">
		<TextPrompt prompt={question.prompt}/>
		<div className={classes.bodyContainer}>
			{ props.children }
			{ questionBody }
		</div>
		{ question.promptType === PromptType.Hint && state !== TriviaStatus.QuestionStart ?
			<HintPrompt question={question} state={state} currentHint={currentHint}/>
			: undefined
		}
		{ state === TriviaStatus.QuestionStart && !gameOptions.selfTimed ?
			<FinalCountdown className={classes.countdown}/>
			: undefined
		}
		<QuestionCounter totalQuestions={totalQuestions} currentQuestion={questionNumber}
						 className={classes.questionCounter}/>
	</div>;
}

type FinalCountdownProps = {
	className:string
}

function FinalCountdown({className}:FinalCountdownProps) {
	const theme:Theme = useTheme();
	const {appState} = useContext(EngageAppContext.current);
	let content = "";
	const countdown = useCountdown(appState.common.timeMark, triviaStateDuration(appState.common), appState.common.pauseMark, TimeUnit.SECONDS);
	if(countdown <= 3) {
		content = "Get ready!";
	}

	return <DisplayText className={className} variant={TextVariant.s24reg} align="center" color={theme.questionProgress.box?.color ?? "#99EFEF"}>
		{content}
	</DisplayText>;
}